export type AsyncTaskStatus =
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "PENDING"
  | "NOT_FOUND";

export const ASYNC_TASK_POLL_MS = 2000;
export const ASYNC_TASK_RUNNING_MAX_MS = 10 * 60 * 1000;
export const ASYNC_TASK_PENDING_MAX_MS = 3 * 60 * 1000;

const TERMINAL_STATUSES: AsyncTaskStatus[] = [
  "SUCCESS",
  "FAILED",
  "NOT_FOUND",
];

export function normalizeAsyncTaskStatus(
  status: string | undefined | null,
): AsyncTaskStatus | null {
  if (!status) return null;
  const upper = String(status).trim().toUpperCase();
  if (upper === "PROCESSING") return "RUNNING";
  if (upper === "DONE") return "SUCCESS";
  if (
    upper === "RUNNING" ||
    upper === "SUCCESS" ||
    upper === "FAILED" ||
    upper === "PENDING" ||
    upper === "NOT_FOUND"
  ) {
    return upper as AsyncTaskStatus;
  }
  return null;
}

export interface PollAsyncTaskOptions<TPayload = void> {
  pollIntervalMs?: number;
  fetchStatus: () => Promise<{
    status?: string;
    message?: string;
    payload?: TPayload;
  } | null | undefined>;
  onPoll?: (args: {
    status: "RUNNING" | "PENDING";
    message?: string;
    payload?: TPayload;
  }) => void;
  onSuccess?: (args: { message?: string; payload?: TPayload }) => void;
  onFailed?: (args: { message?: string; payload?: TPayload }) => void;
  onNotFound?: () => void;
  onTimeout?: () => void;
  onStopped?: () => void;
}

export interface PollAsyncTaskHandle {
  stop: () => void;
}

/**
 * Polls an async task until a terminal status, timeout, or stop() is called.
 * Status matching is case-insensitive.
 */
export function startAsyncTaskPoll<TPayload = void>(
  options: PollAsyncTaskOptions<TPayload>,
): PollAsyncTaskHandle {
  const {
    pollIntervalMs = ASYNC_TASK_POLL_MS,
    fetchStatus,
    onPoll,
    onSuccess,
    onFailed,
    onNotFound,
    onTimeout,
    onStopped,
  } = options;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let runningSince: number | null = null;
  let pendingSince: number | null = null;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimer();
    onStopped?.();
  };

  const scheduleNext = () => {
    if (stopped) return;
    timer = setTimeout(runPoll, pollIntervalMs);
  };

  const handleTerminal = (status: AsyncTaskStatus, result: {
    message?: string;
    payload?: TPayload;
  }) => {
    if (status === "SUCCESS") {
      onSuccess?.(result);
    } else if (status === "FAILED") {
      onFailed?.(result);
    } else if (status === "NOT_FOUND") {
      onNotFound?.();
    }
    stop();
  };

  const runPoll = async () => {
    if (stopped) return;

    try {
      const raw = await fetchStatus();
      if (stopped) return;

      const status = normalizeAsyncTaskStatus(raw?.status);
      if (!status) {
        scheduleNext();
        return;
      }

      if (status === "RUNNING") {
        if (runningSince === null) runningSince = Date.now();
        pendingSince = null;
        if (Date.now() - runningSince > ASYNC_TASK_RUNNING_MAX_MS) {
          stop();
          onTimeout?.();
          return;
        }
        onPoll?.({
          status: "RUNNING",
          message: raw?.message,
          payload: raw?.payload,
        });
        scheduleNext();
        return;
      }

      if (status === "PENDING") {
        if (pendingSince === null) pendingSince = Date.now();
        runningSince = null;
        if (Date.now() - pendingSince > ASYNC_TASK_PENDING_MAX_MS) {
          stop();
          onTimeout?.();
          return;
        }
        onPoll?.({
          status: "PENDING",
          message: raw?.message,
          payload: raw?.payload,
        });
        scheduleNext();
        return;
      }

      if (TERMINAL_STATUSES.includes(status)) {
        handleTerminal(status, {
          message: raw?.message,
          payload: raw?.payload,
        });
      }
    } catch {
      if (!stopped) scheduleNext();
    }
  };

  runPoll();

  return { stop };
}
